import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Search, MapPin, Loader2 } from "lucide-react";

/**
 * MapboxSearch — sticky autocomplete coordinate picker.
 * Calls Mapbox Geocoding API directly with REACT_APP_MAPBOX_TOKEN.
 *
 * Props:
 *  - onSelect({ lng, lat, name }) — fires when user picks a suggestion
 *  - label  — caption shown above input
 *  - placeholder
 *  - testid  — base data-testid
 */
export default function MapboxSearch({ onSelect, label = "Yer axtarışı", placeholder = "Şəhər, küçə və ya yer...", testid = "geocode" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);
  const token = process.env.REACT_APP_MAPBOX_TOKEN;

  useEffect(() => {
    const handle = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.length < 2 || !token) {
      setResults([]);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${token}&country=AZ&language=az,en&limit=6&types=place,locality,address,poi,district,region`;
        const r = await fetch(url);
        const data = await r.json();
        const features = (data.features || []).map((f) => ({
          name: f.place_name,
          text: f.text,
          lng: f.center[0],
          lat: f.center[1],
          type: (f.place_type || ["?"])[0],
        }));
        setResults(features);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, token]);

  const pick = (f) => {
    onSelect?.(f);
    setQ(f.text || f.name);
    setOpen(false);
  };

  if (!token) {
    return (
      <div className="text-[10px] mono text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        Mapbox token tapılmadı — axtarış deaktivdir
      </div>
    );
  }

  return (
    <div ref={wrap} className="relative" data-testid={`${testid}-wrap`}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="pl-8 pr-8"
          data-testid={`${testid}-input`}
        />
        {loading && (
          <Loader2 className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
        )}
      </div>
      {open && results.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {results.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(f)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-start gap-2"
              data-testid={`${testid}-item-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-[#FF5722] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.text}</div>
                <div className="text-[10px] mono text-slate-500 truncate">
                  {f.name}
                </div>
              </div>
              <span className="mono text-[9px] uppercase text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                {f.type}
              </span>
            </button>
          ))}
        </div>
      )}
      {label && (
        <div className="mt-1 text-[10px] mono text-slate-400">
          Tapdığınız ünvanı vurun → koordinatlar avtomatik dolur
        </div>
      )}
    </div>
  );
}
